// React
import React, { useEffect, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';

// Components
import { MpsCheckBox, MpsButton } from '@mobilitystore/components-interface';
import Loader from '../Shared/Utils/Loader';

// react-pdf related js and css
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.mjs`;
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Small buffer so rounding on fractional scroll heights still counts as "reached the end".
const SCROLL_END_BUFFER = 8;

const LoanConsentModal = ({ isOpen, isSubmitting, onClose, onAccept, content, documentUrl, dataProtectionDocumentUrl }) => {
  const bodyRef = useRef(null);
  const [numPages, setNumPages] = useState(0);
  const [hasReadToEnd, setHasReadToEnd] = useState(false);
  const [isConsentChecked, setIsConsentChecked] = useState(false);
  const [isDataProtectionChecked, setIsDataProtectionChecked] = useState(false);
  const [hasDocumentLoadError, setHasDocumentLoadError] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setNumPages(0);
      setHasReadToEnd(false);
      setIsConsentChecked(false);
      setIsDataProtectionChecked(false);
      setHasDocumentLoadError(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const markReadIfScrolledToEnd = (element) => {
    if (!element) return;
    const { scrollTop, scrollHeight, clientHeight } = element;
    if (scrollHeight - clientHeight <= SCROLL_END_BUFFER || scrollTop + clientHeight >= scrollHeight - SCROLL_END_BUFFER) {
      setHasReadToEnd(true);
    }
  };

  const onDocumentLoadSuccess = ({ numPages: loadedPages }) => {
    setNumPages(loadedPages);
  };

  // Without this the loader spins forever if the document can't be fetched/rendered.
  const onDocumentLoadError = () => {
    setHasDocumentLoadError(true);
  };

  // Content shorter than the viewport has nothing to scroll, so unlock as soon as it renders.
  const handlePageRenderSuccess = () => {
    markReadIfScrolledToEnd(bodyRef.current);
  };

  const handleScroll = (event) => {
    markReadIfScrolledToEnd(event.target);
  };

  const handleConsentCheckboxChange = ({ value }) => {
    if (hasReadToEnd) setIsConsentChecked(value);
  };

  const handleDataProtectionCheckboxChange = ({ value }) => {
    if (hasReadToEnd) setIsDataProtectionChecked(value);
  };

  return (
    <div className='loan-consent-modal'>
      <dialog
        open
        className='m-dialog -floating-shadow-s -floating loan-consent-modal__dialog'
        aria-labelledby='loan-consent-modal-title'
      >
        <div className='m-dialog__header'>
          <div className='m-dialog__title' id='loan-consent-modal-title'>
            {content?.title}
          </div>
          <button
            type='button'
            className='a-button a-button--integrated -without-label'
            aria-label={content?.close_aria_label}
            disabled={isSubmitting}
            onClick={onClose}
          >
            <i className='a-icon a-button__icon ui-ic-close'></i>
          </button>
        </div>
        <div className='m-dialog__content'>
          <div
            ref={bodyRef}
            className='loan-consent-modal__body'
            onScroll={handleScroll}
          >
            <Document
              file={documentUrl}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
              loading={<Loader />}
            >
              {Array.from({ length: numPages }, (_page, index) => index + 1).map(
                (page) => (
                  <Page
                    key={page}
                    pageNumber={page}
                    width={bodyRef.current?.clientWidth}
                    wrap={false}
                    loading=''
                    onRenderSuccess={handlePageRenderSuccess}
                  />
                )
              )}
            </Document>
            {hasDocumentLoadError && (
              <div className='loan-consent-modal__document-error' role='alert'>
                {content?.document_load_error}
              </div>
            )}
          </div>

          <div className='loan-consent-modal__footer'>
            <MpsCheckBox
              id='loan-terms-conditions-checkbox'
              className='loan-consent-modal__checkbox'
              checked={isConsentChecked}
              disabled={!hasReadToEnd || isSubmitting}
              displayName={
                hasReadToEnd
                  ? content?.checkbox_label_enabled
                  : content?.checkbox_label_disabled
              }
              handleChange={handleConsentCheckboxChange}
            />
            <div className='loan-consent-modal__data-protection-consent'>
              <p>{content?.introduction}</p>
              <ul>
                {content?.purposes?.map((purpose) => (
                  <li key={purpose}>{purpose}</li>
                ))}
              </ul>
              <p>{content?.withdrawal}</p>
              <p>
                {content?.notice_prefix}{' '}
                <a
                  href={dataProtectionDocumentUrl}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='loan-consent-modal__link'
                >
                  {content?.notice_link_label}
                </a>{' '}
                {content?.notice_suffix}
              </p>
            </div>
            <MpsCheckBox
              id='loan-data-protection-consent-checkbox'
              className='loan-consent-modal__checkbox loan-consent-modal__checkbox--consent'
              checked={isDataProtectionChecked}
              disabled={!hasReadToEnd || isSubmitting}
              displayName={content?.checkbox_label}
              handleChange={handleDataProtectionCheckboxChange}
            />
            <div className='m-dialog__actions'>
              <MpsButton
                className='a-button--primary'
                label={isSubmitting ? content?.proceed_button_loading : content?.proceed_button}
                mode='primary'
                disabled={!isConsentChecked || !isDataProtectionChecked || isSubmitting}
                onClick={onAccept}
              />
            </div>
          </div>
        </div>
      </dialog>
    </div>
  );
};

export default LoanConsentModal;
